import { FleetMilestone, HowStep, PlanPhase, SummaryMetrics, WhyCard } from "@/lib/orplanet/types";

export const heroMilestones: FleetMilestone[] = [
    { year: 2025, gas: 760, ev: 0, label: "Baseline" },
    { year: 2028, gas: 730, ev: 30, label: "Phase 1" },
    { year: 2030, gas: 700, ev: 60, label: "Phase 2" },
    { year: 2033, gas: 600, ev: 160, label: "Phase 3" },
    { year: 2035, gas: 448, ev: 312, label: "Phase 4" },
    { year: 2038, gas: 150, ev: 610, label: "Phase 5" },
    { year: 2040, gas: 0, ev: 760, label: "Net Zero" },
];

export const whyCards: WhyCard[] = [
    {
        title: "Reduce Emissions",
        description: "Cut fleet carbon footprint by up to 80% using phased electrification.",
    },
    {
        title: "Lower Fuel Costs",
        description: "EVs reduce per-mile cost while improving total cost of ownership.",
    },
    {
        title: "Meet Regulations",
        description: "Stay ahead of rapidly tightening regional and national mandates.",
    },
    {
        title: "Sustainability Goals",
        description: "Show measurable ESG progress with auditable carbon metrics.",
    },
    {
        title: "Fleet Optimization",
        description: "Prioritize transitions by route, mileage, and infrastructure fit.",
    },
    {
        title: "Carbon Credits",
        description: "Bridge interim emissions while fleet electrification scales.",
    },
];

export const howSteps: HowStep[] = [
    {
        step: "01",
        title: "Upload Fleet Data",
        description: "Share vehicle, route, and mileage data from CSV or Excel.",
    },
    {
        step: "02",
        title: "AI Optimization",
        description: "Run thousands of scenarios to optimize transition impact and ROI.",
    },
    {
        step: "03",
        title: "Get Roadmap",
        description: "Receive phased decarbonization timeline with savings projections.",
    },
];

export const sampleRows: string[][] = [
    ["Vehicle_ID", "Type", "Fuel", "Year", "Annual_Miles", "Route", "Depot", "Status"],
    ["TRK-001", "Class 8 Truck", "Diesel", "2019", "85,000", "Long Haul I-95", "Atlanta", "Active"],
    ["TRK-002", "Class 8 Truck", "Diesel", "2020", "78,000", "Long Haul I-10", "Houston", "Active"],
    ["VAN-003", "Cargo Van", "Gasoline", "2018", "42,000", "Urban Delivery", "Chicago", "Active"],
    ["TRK-760", "Class 8 Truck", "Diesel", "2020", "81,000", "Long Haul I-80", "Omaha", "Active"],
];

export function generatePlan(totalFleet: number): PlanPhase[] {
    const phase1 = Math.round(totalFleet * 0.04);
    const phase2 = Math.round(totalFleet * 0.04);
    const phase3 = Math.round(totalFleet * 0.13);
    const phase4 = Math.round(totalFleet * 0.2);
    const phase5 = Math.round(totalFleet * 0.39);
    const finalPhase = totalFleet - phase1 - phase2 - phase3 - phase4 - phase5;

    return [
        {
            year: 2026,
            type: "audit",
            title: "Fleet Audit & Baseline",
            evCount: 0,
            totalFleet,
            description: `Establish baseline emissions and charging infrastructure requirements for ${totalFleet} vehicles.`,
        },
        {
            year: 2028,
            type: "ev",
            title: "Phase 1 — EV Transition",
            evCount: phase1,
            totalFleet,
            description: `Transition ${phase1} high-mileage urban-route vehicles to EVs.`,
        },
        {
            year: 2029,
            type: "credit",
            title: "Carbon Credit Purchase",
            evCount: 0,
            totalFleet,
            description: "Offset residual emissions with verified carbon credit portfolio.",
        },
        {
            year: 2030,
            type: "ev",
            title: "Phase 2 — EV Expansion",
            evCount: phase2,
            totalFleet,
            description: `Electrify ${phase2} additional medium-duty vehicles and depots.`,
        },
        {
            year: 2033,
            type: "ev",
            title: "Phase 3 — Major Scale-Up",
            evCount: phase3,
            totalFleet,
            description: `Scale electrification with ${phase3} additional vehicle transitions.`,
        },
        {
            year: 2035,
            type: "ev",
            title: "Phase 4 — Heavy Electrification",
            evCount: phase4,
            totalFleet,
            description: `Add ${phase4} electric vehicles in regional and heavy duty segments.`,
        },
        {
            year: 2038,
            type: "ev",
            title: "Phase 5 — Near Complete",
            evCount: phase5,
            totalFleet,
            description: `Transition ${phase5} vehicles to achieve near-total electrification.`,
        },
        {
            year: 2040,
            type: "target",
            title: "100% Zero-Emission Fleet",
            evCount: finalPhase,
            totalFleet,
            description: `Transition final ${finalPhase} vehicles to reach 100% electric fleet.`,
        },
    ];
}

export function getSummaryMetrics(totalFleet: number): SummaryMetrics {
    return {
        estimatedCo2ReductionTons: Math.round(totalFleet * 12.4),
        estimatedFuelSavingsUsd: Math.round(totalFleet * 4200),
    };
}
